import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Globe, 
  Rocket, 
  CheckCircle, 
  XCircle, 
  Clock,
  RefreshCw,
  ExternalLink,
  MoreVertical,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Terminal,
  Download,
  Pause,
  Play,
  RotateCcw
} from 'lucide-react';
import { apiClient } from '@/services/api';
import { useBuildJobWebSocket } from '@/hooks/useWebSocket';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/utils/cn';
import { Deployment } from '@/types';

export default function Deployments() {
  const [selectedDeployment, setSelectedDeployment] = useState<string | null>(null);
  const [logsFilter, setLogsFilter] = useState<'all' | 'error' | 'warning'>('all');

  // Fetch all applications with deployments
  const { data: applications, isLoading } = useQuery({
    queryKey: ['applications-with-deployments'],
    queryFn: () => apiClient.getApplications({ limit: 50 }),
  });

  // Real-time build status for selected deployment
  const { job, logs, progress } = useBuildJobWebSocket(selectedDeployment || undefined);

  const getStatusColor = (status: Deployment['status']) => {
    switch (status) {
      case 'live':
        return 'text-green-500 bg-green-500/10 border-green-500/20';
      case 'building':
      case 'deploying':
        return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
      case 'failed':
        return 'text-red-500 bg-red-500/10 border-red-500/20';
      default:
        return 'text-gray-500 bg-gray-500/10 border-gray-500/20';
    }
  };

  const getStatusIcon = (status: Deployment['status']) => {
    switch (status) {
      case 'live':
        return <CheckCircle className="h-4 w-4" />;
      case 'building':
      case 'deploying':
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : Date.now();
    const duration = Math.floor((endTime - startTime) / 1000);
    
    if (duration < 60) return `${duration}s`;
    if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  // Mock deployment data
  const mockDeployments: Deployment[] = [
    {
      id: '1',
      applicationId: 'app1',
      version: 'v1.2.3',
      status: 'live',
      url: 'https://app1.zewst.app',
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      completedAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
      metrics: {
        buildTime: 120,
        deployTime: 45,
        size: 2048000,
      },
    },
    {
      id: '2',
      applicationId: 'app2',
      version: 'v2.0.0',
      status: 'building',
      createdAt: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
      metrics: {
        buildTime: 0,
        deployTime: 0,
        size: 0,
      },
    },
    {
      id: '3',
      applicationId: 'app3',
      version: 'v0.9.1',
      status: 'failed',
      createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      completedAt: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
      metrics: {
        buildTime: 300,
        deployTime: 0,
        size: 0,
      },
    },
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Deployments</h1>
          <p className="text-muted-foreground mt-1">
            Manage and monitor your application deployments
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Logs
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Deployments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">47</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500">+12</span> this week
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Live Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">12</div>
            <p className="text-xs text-muted-foreground">All systems operational</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">94%</div>
            <Progress value={94} className="mt-2 h-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Deploy Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2m 34s</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500">-15s</span> from last month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Deployments Table/List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Recent Deployments</CardTitle>
            <Tabs defaultValue="all" className="w-auto">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="live">Live</TabsTrigger>
                <TabsTrigger value="building">Building</TabsTrigger>
                <TabsTrigger value="failed">Failed</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {mockDeployments.map((deployment) => (
                <div
                  key={deployment.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={cn("p-2 rounded-full", getStatusColor(deployment.status))}>
                      {getStatusIcon(deployment.status)}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">Application Name</p>
                        <Badge variant="outline" className="text-xs">
                          {deployment.version}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span>{formatDate(deployment.createdAt)}</span>
                        {deployment.status === 'live' && deployment.url && (
                          <>
                            <span>•</span>
                            <a
                              href={deployment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 hover:text-primary"
                            >
                              {deployment.url.replace(/^https?:\/\//, '')}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </>
                        )}
                        {deployment.metrics?.buildTime && (
                          <>
                            <span>•</span>
                            <span>Build: {deployment.metrics.buildTime}s</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {deployment.status === 'building' && (
                      <div className="flex items-center gap-2 mr-4">
                        <Progress value={progress || 33} className="w-24 h-2" />
                        <span className="text-sm text-muted-foreground">{progress || 33}%</span>
                      </div>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedDeployment(deployment.id)}
                    >
                      <Terminal className="h-4 w-4 mr-1" />
                      Logs
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {deployment.status === 'live' && (
                          <>
                            <DropdownMenuItem>
                              <Globe className="h-4 w-4 mr-2" />
                              View Live
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Pause className="h-4 w-4 mr-2" />
                              Stop Deployment
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        {deployment.status === 'failed' && (
                          <>
                            <DropdownMenuItem>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Retry Deployment
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem>
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Rollback
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Download className="h-4 w-4 mr-2" />
                          Download Logs
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs Panel */}
      {selectedDeployment && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Deployment Logs</CardTitle>
              <div className="flex gap-2">
                <Tabs value={logsFilter} onValueChange={(v) => setLogsFilter(v as any)}>
                  <TabsList>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="error">Errors</TabsTrigger>
                    <TabsTrigger value="warning">Warnings</TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedDeployment(null)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96 w-full rounded-md border bg-black p-4">
              <div className="font-mono text-xs text-green-400">
                {logs.length > 0 ? (
                  logs.map((log, index) => (
                    <div key={index} className="mb-1">
                      <span className="text-gray-500">[{new Date().toISOString()}]</span> {log}
                    </div>
                  ))
                ) : (
                  <>
                    <div>[2024-01-20 10:23:45] Starting deployment...</div>
                    <div>[2024-01-20 10:23:46] Building application...</div>
                    <div>[2024-01-20 10:23:47] Installing dependencies...</div>
                    <div>[2024-01-20 10:23:52] Running build scripts...</div>
                    <div>[2024-01-20 10:24:15] Build completed successfully</div>
                    <div>[2024-01-20 10:24:16] Uploading artifacts...</div>
                    <div>[2024-01-20 10:24:25] Configuring deployment...</div>
                    <div>[2024-01-20 10:24:30] Deployment successful!</div>
                    <div>[2024-01-20 10:24:31] Application is now live at https://app.zewst.app</div>
                  </>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}